import {t, Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useLocalize} from '../../../../../../../srcCommon/internationalization/internationalizationContext';
import {useAppSelector} from '../../../../../../helpers/reduxHelpers';
import {ShopifyIntegrationSettingsStoreModel} from '../../../../../../models/api/integrationModel';
import {getIsUserGlobalAdmin} from '../../../../../../store/data/teammates/teammatesSelectors';
import {maybeRenderConfigureDialogTextBox} from '../shared/settingsConfigureHelpers';

/*
 * Props.
 */

interface SettingsAppsConfigureShopifyStoreProps {
  index: number;
  store: ShopifyIntegrationSettingsStoreModel;
  shouldAllowRemovingStore: boolean;
  isDecrypting: boolean;
  onRemoveStore: HandlerOf<number>;
  onUpdateStore: (store: ShopifyIntegrationSettingsStoreModel, index: number) => void;
}

/*
 * Style.
 */

const StyledStoreWrapperDiv = styled.div`
  border-radius: 8px;
  border: 1px solid ${(p) => p.theme.greys.shade40};
  padding: 8px;

  &:not(:last-child) {
    margin-bottom: 12px;
  }
`;

const StyledStoreTitleWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const StyledStoreTitleDiv = styled.div`
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.semibold};
  flex: 1;
`;

/*
 * Component.
 */

export const SettingsAppsConfigureShopifyStore: FC<SettingsAppsConfigureShopifyStoreProps> = (props) => {
  const {index, store, shouldAllowRemovingStore, isDecrypting, onRemoveStore, onUpdateStore} = props;
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const localize = useLocalize();

  return (
    <StyledStoreWrapperDiv>
      <StyledStoreTitleWrapperDiv>
        <StyledStoreTitleDiv>
          <Trans id="settings-apps-configure-app-dialog-shopify-store-title">Store #{index + 1}</Trans>
        </StyledStoreTitleDiv>
        <IconButton
          iconName="trashSmall"
          isDisabled={!shouldAllowRemovingStore || isDecrypting || !isGlobalAdmin}
          label={<Trans id="settings-apps-configure-app-dialog-shopify-remove-store">Remove store</Trans>}
          onClick={() => onRemoveStore(index)}
        />
      </StyledStoreTitleWrapperDiv>
      {maybeRenderConfigureDialogTextBox(
        store.apiDomain || '',
        <Trans id="settings-apps-configure-app-dialog-shopify-store-subdomain">Store subdomain</Trans>,
        (value) => {
          onUpdateStore(
            {
              ...store,
              apiDomain: value.trim(),
            },
            index,
          );
        },
        {
          isDisabled: !isGlobalAdmin,
          isLoading: isDecrypting && isGlobalAdmin,
          isRequired: true,
          placeholder: localize(
            t(
              'settings-apps-configure-app-dialog-shopify-store-subdomain-placeholder',
            )`shop-name from shop-name.myshopify.com`,
          ),
        },
      )}
      {maybeRenderConfigureDialogTextBox(
        store.apiKey || '',
        <Trans id="settings-apps-configure-app-dialog-shopify-store-api-key">API key</Trans>,
        (value) => {
          onUpdateStore(
            {
              ...store,
              apiKey: value.trim(),
            },
            index,
          );
        },
        {
          isDisabled: !isGlobalAdmin,
          isLoading: isDecrypting && isGlobalAdmin,
          isRequired: true,
        },
      )}
      {maybeRenderConfigureDialogTextBox(
        store.apiPassword || '',
        <Trans id="settings-apps-configure-app-dialog-shopify-store-api-password">API password</Trans>,
        (value) => {
          onUpdateStore(
            {
              ...store,
              apiPassword: value,
            },
            index,
          );
        },
        {
          isDisabled: !isGlobalAdmin,
          isLoading: isDecrypting && isGlobalAdmin,
          isRequired: true,
        },
      )}
    </StyledStoreWrapperDiv>
  );
};
